import React from "react";
import { useNavigate } from "react-router";
import { Plus, Pencil, Trash2 } from "lucide-react";
import {
  EquipmentDto,
  MaintenanceAreaDto,
  equipmentApi,
  maintenanceAreasApi,
} from "../../../services/api";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { EquipmentFormDialog } from "./EquipmentFormDialog";
import { formatDate } from "../utils";

export const EquipmentListPage: React.FC = () => {
  const navigate = useNavigate();
  const [areas, setAreas] = React.useState<MaintenanceAreaDto[]>([]);
  const [items, setItems] = React.useState<EquipmentDto[]>([]);
  const [loading, setLoading] = React.useState(true);
  const [error, setError] = React.useState<string | null>(null);

  const [filterAreaId, setFilterAreaId] = React.useState<string>("");
  const [search, setSearch] = React.useState("");
  const [creating, setCreating] = React.useState(false);
  const [editing, setEditing] = React.useState<EquipmentDto | null>(null);

  const reload = React.useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const [a, eq] = await Promise.all([
        maintenanceAreasApi.getAll(false),
        equipmentApi.search({
          areaId: filterAreaId || undefined,
          search: search.trim() || undefined,
        }),
      ]);
      setAreas(a);
      setItems(eq);
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setLoading(false);
    }
  }, [filterAreaId, search]);

  React.useEffect(() => { void reload(); }, [reload]);

  const handleDelete = async (eq: EquipmentDto) => {
    const cascadeMsg = eq.activeTaskCount > 0
      ? `\n\nSe desactivarán también sus ${eq.activeTaskCount} tarea(s) asociada(s).`
      : "";
    if (!window.confirm(`¿Eliminar el equipo "${eq.name}"?${cascadeMsg}\n\nEs un borrado lógico: podrás recuperarlo desde administración si hace falta.`)) return;
    try {
      await equipmentApi.delete(eq.id);
      await reload();
    } catch (e) {
      window.alert(e instanceof Error ? e.message : String(e));
    }
  };

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap gap-2 items-end">
        <div className="flex-1 min-w-[200px]">
          <label className="text-xs text-slate-500">Buscar</label>
          <Input
            placeholder="Nombre, código, nº de serie…"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
        </div>
        <div className="w-56">
          <label className="text-xs text-slate-500">Área</label>
          <Select value={filterAreaId || "_all"} onValueChange={(v) => setFilterAreaId(v === "_all" ? "" : v)}>
            <SelectTrigger>
              <SelectValue placeholder="Todas" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="_all">Todas</SelectItem>
              {areas.map((a) => (
                <SelectItem key={a.id} value={a.id}>{a.name}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <div className="ml-auto">
          <Button onClick={() => setCreating(true)} disabled={areas.length === 0}>
            <Plus className="size-4 mr-2" /> Nuevo equipo
          </Button>
        </div>
      </div>

      {areas.length === 0 && (
        <Card className="p-4 text-sm text-amber-700 bg-amber-50 border-amber-200">
          Crea primero al menos un área antes de añadir equipos.
        </Card>
      )}

      {error && (
        <div className="rounded border border-red-200 bg-red-50 text-red-700 px-3 py-2 text-sm">{error}</div>
      )}

      {loading ? (
        <div className="text-sm text-slate-500">Cargando…</div>
      ) : items.length === 0 ? (
        <Card className="p-6 text-center text-slate-500 text-sm">
          No hay equipos con esos filtros.
        </Card>
      ) : (
        <div className="overflow-x-auto rounded border bg-white">
          <table className="w-full text-sm">
            <thead className="bg-slate-50 text-slate-600 text-xs uppercase">
              <tr>
                <th className="text-left px-3 py-2">Nombre</th>
                <th className="text-left px-3 py-2">Área</th>
                <th className="text-left px-3 py-2">Tipo</th>
                <th className="text-left px-3 py-2">Ubicación</th>
                <th className="text-left px-3 py-2">Tareas</th>
                <th className="text-left px-3 py-2">Próxima</th>
                <th className="px-3 py-2 w-24"></th>
              </tr>
            </thead>
            <tbody>
              {items.map((e) => (
                <tr
                  key={e.id}
                  className="border-t hover:bg-slate-50 cursor-pointer"
                  onClick={() => navigate(`/mantenimiento/equipos/${e.slug}`)}
                >
                  <td className="px-3 py-2 font-medium">
                    {e.name}
                    {e.code && <span className="text-xs text-slate-500 ml-2">[{e.code}]</span>}
                    <div className="text-[10px] text-slate-400 font-mono mt-0.5">/{e.slug}</div>
                  </td>
                  <td className="px-3 py-2">{e.areaName}</td>
                  <td className="px-3 py-2">{e.type ?? "—"}</td>
                  <td className="px-3 py-2">{e.location ?? "—"}</td>
                  <td className="px-3 py-2">{e.activeTaskCount}</td>
                  <td className="px-3 py-2">{formatDate(e.nextTaskDueAt, false)}</td>
                  <td className="px-3 py-2 text-right whitespace-nowrap" onClick={(ev) => ev.stopPropagation()}>
                    <Button size="sm" variant="ghost" onClick={() => setEditing(e)}>
                      <Pencil className="size-3.5" />
                    </Button>
                    <Button
                      size="sm"
                      variant="ghost"
                      className="text-red-600 hover:text-red-700"
                      onClick={() => handleDelete(e)}
                    >
                      <Trash2 className="size-3.5" />
                    </Button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {(creating || editing) && (
        <EquipmentFormDialog
          equipment={editing}
          areas={areas}
          onClose={() => { setCreating(false); setEditing(null); }}
          onSaved={() => { setCreating(false); setEditing(null); void reload(); }}
        />
      )}
    </div>
  );
};
